import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Vote, CalendarCheck, Activity, MessageSquareHeart, CalendarSync, Users, Shuffle } from "lucide-react";
import {
  localizedHref,
  useMarketingLocale,
  type SupportedMarketingLocale,
} from "@/i18n/marketing";

export type SisterFeatureKey = "home" | "rsvp" | "rpe" | "feedback" | "fixtures" | "attendance" | "subManager";

type LocalizedString = Record<SupportedMarketingLocale, string>;

interface FeatureDef {
  key: SisterFeatureKey;
  path: string;
  icon: typeof Vote;
  label: LocalizedString;
  desc: LocalizedString;
}

const FEATURES: FeatureDef[] = [
  {
    key: "home",
    path: "/",
    icon: Vote,
    label: {
      en: "Player's Player Voting",
      es: "Votación del jugador",
      "pt-BR": "Votação dos jogadores",
      fr: "Vote du joueur",
      ja: "選手間投票（MVP）",
    },
    desc: {
      en: "One-tap MVP polls. Players vote without an account.",
      es: "Encuestas MVP con un toque. Los jugadores votan sin cuenta.",
      "pt-BR": "Enquetes do craque com um toque. Os jogadores votam sem conta.",
      fr: "Sondages MVP en un clic. Les joueurs votent sans compte.",
      ja: "ワンタップのMVP投票。選手はアカウントなしで投票できます。",
    },
  },
  {
    key: "rsvp",
    path: "/rsvp-info",
    icon: CalendarCheck,
    label: {
      en: "Game RSVP",
      es: "Asistencia al partido",
      "pt-BR": "Presença no jogo",
      fr: "Présence au match",
      ja: "試合の出欠確認",
    },
    desc: {
      en: "WhatsApp-native RSVP. No app, no login, no nagging.",
      es: "Asistencia desde WhatsApp. Sin app, sin login, sin presión.",
      "pt-BR": "Confirmação no WhatsApp. Sem app, sem login, sem cobrança.",
      fr: "Confirmation dans WhatsApp. Sans app, sans login, sans relance.",
      ja: "WhatsAppやLINEでそのまま出欠確認。アプリ・ログイン・催促は不要。",
    },
  },
  {
    key: "rpe",
    path: "/rpe-info",
    icon: Activity,
    label: {
      en: "Exertion Tracking (RPE)",
      es: "Monitoreo de carga (RPE)",
      "pt-BR": "Monitoramento de carga (PSE)",
      fr: "Suivi de charge (RPE)",
      ja: "運動負荷トラッキング（RPE）",
    },
    desc: {
      en: "Pro-grade load monitoring with one-tap player responses.",
      es: "Monitoreo de carga profesional con respuestas en un toque.",
      "pt-BR": "Monitoramento de carga profissional com resposta em um toque.",
      fr: "Suivi de charge pro avec réponses joueur en un clic.",
      ja: "プロ仕様の負荷モニタリング。選手はワンタップで回答。",
    },
  },
  {
    key: "feedback",
    path: "/feedback-info",
    icon: MessageSquareHeart,
    label: {
      en: "Coach Feedback",
      es: "Evaluación del entrenador",
      "pt-BR": "Avaliação do treinador",
      fr: "Évaluation de l'entraîneur",
      ja: "コーチ評価",
    },
    desc: {
      en: "Anonymous, structured feedback after every match.",
      es: "Evaluación anónima y estructurada tras cada partido.",
      "pt-BR": "Feedback anônimo e estruturado após cada jogo.",
      fr: "Feedback anonyme et structuré après chaque match.",
      ja: "毎試合後に集める、匿名で構造化されたフィードバック。",
    },
  },
  {
    key: "fixtures",
    path: "/fixtures-info",
    icon: CalendarSync,
    label: {
      en: "Fixture Autopilot",
      es: "Piloto automático de partidos",
      "pt-BR": "Piloto automático de jogos",
      fr: "Pilote auto. des matchs",
      ja: "試合日程オートパイロット",
    },
    desc: {
      en: "We crawl your league. You stop typing fixtures.",
      es: "Rastreamos tu liga. Tú dejas de tipear partidos.",
      "pt-BR": "Rastreamos sua liga. Você para de digitar jogos.",
      fr: "Nous explorons votre ligue. Plus de saisie manuelle.",
      ja: "リーグの試合日程を自動取得。手入力から解放されます。",
    },
  },
  {
    key: "attendance",
    path: "/attendance-info",
    icon: Users,
    label: {
      en: "Team Attendance",
      es: "Asistencia del equipo",
      "pt-BR": "Presença do time",
      fr: "Présence de l'équipe",
      ja: "チームの出席管理",
    },
    desc: {
      en: "Mobile-first roll-call for training and matches. Spot absence trends early.",
      es: "Pase de lista móvil para entrenos y partidos. Detecta tendencias de ausencia.",
      "pt-BR": "Lista de presença mobile-first para treinos e jogos. Detecte tendências cedo.",
      fr: "Appel mobile-first pour entraînements et matchs. Repérez les tendances d'absence.",
      ja: "練習・試合のスマホ点呼。欠席傾向を早めに把握。",
    },
  },
  {
    key: "subManager",
    path: "https://club.gameon.futbol/sub-manager",
    icon: Shuffle,
    label: {
      en: "Sub Manager",
      es: "Gestor de cambios",
      "pt-BR": "Gerenciador de substituições",
      fr: "Gestionnaire des remplacements",
      ja: "選手交代マネージャー",
    },
    desc: {
      en: "Fair playing time, automated. Plan subs in seconds — no spreadsheets.",
      es: "Tiempo de juego justo, automatizado. Planifica cambios en segundos.",
      "pt-BR": "Tempo de jogo justo, automatizado. Planeje substituições em segundos.",
      fr: "Temps de jeu équitable, automatisé. Planifiez les changements en quelques secondes.",
      ja: "公平な出場時間を自動化。数秒で交代プラン、表計算はもう不要。",
    },
  },
];

const HEADING: LocalizedString = {
  en: "Explore the rest of Vote.GameOn",
  es: "Conoce el resto de Vote.GameOn",
  "pt-BR": "Conheça o resto do Vote.GameOn",
  fr: "Découvrez le reste de Vote.GameOn",
  ja: "Vote.GameOnの他の機能を見る",
};
const SUBHEAD: LocalizedString = {
  en: "Built to work together. Use one or all four — they share the same player roster and zero-friction philosophy.",
  es: "Diseñadas para trabajar juntas. Usa una o las cuatro — comparten la misma plantilla y filosofía sin fricción.",
  "pt-BR": "Feitas para funcionar juntas. Use uma ou as quatro — compartilham o mesmo elenco e a mesma filosofia sem atrito.",
  fr: "Conçus pour fonctionner ensemble. Utilisez-en un ou les quatre — ils partagent le même effectif et la même philosophie sans friction.",
  ja: "連携するように作られています。ひとつでも全部でもOK — 同じ選手名簿と「摩擦ゼロ」の思想を共有します。",
};
const LEARN_MORE: LocalizedString = {
  en: "Learn more",
  es: "Saber más",
  "pt-BR": "Saiba mais",
  fr: "En savoir plus",
  ja: "詳しく見る",
};

export function SisterFeatureLinks({ current }: { current: SisterFeatureKey }) {
  const locale = useMarketingLocale();
  const others = FEATURES.filter((f) => f.key !== current);

  return (
    <section className="py-16 lg:py-20 border-t border-border/40">
      <div className="container max-w-5xl mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="heading-2 text-foreground mb-3">{HEADING[locale]}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">{SUBHEAD[locale]}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {others.map((f, i) => {
            const Icon = f.icon;
            const isExternal = /^https?:\/\//.test(f.path);
            const cardClass =
              "group block h-full rounded-2xl border border-border bg-card/50 backdrop-blur-sm p-5 hover:border-primary/60 hover:bg-card hover:shadow-lg hover:-translate-y-0.5 transition-all";
            const inner = (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">{f.label[locale]}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{f.desc[locale]}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
                  {LEARN_MORE[locale]}
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </>
            );
            return (
              <motion.div
                key={f.key}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                {isExternal ? (
                  <a href={f.path} target="_blank" rel="noopener noreferrer" className={cardClass}>
                    {inner}
                  </a>
                ) : (
                  <Link to={localizedHref(f.path, locale)} className={cardClass}>
                    {inner}
                  </Link>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
